import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import EmailTemplatesSection from "@/components/EmailTemplatesSection";
import AdminUsersSection from "@/components/AdminUsersSection";
import ProjectTypesSection from "@/components/ProjectTypesSection";
import api from "@/lib/api";
import { toast } from "sonner";
import { Envelope, Key, TestTube, CheckCircle, Warning, Database, Sparkle, FileText, Copy } from "@phosphor-icons/react";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [sender, setSender] = useState("");
  const [senderName, setSenderName] = useState("");
  const [llmKey, setLlmKey] = useState("");
  const [anthropicKey, setAnthropicKey] = useState("");
  const [aiUsage, setAiUsage] = useState(null);
  const [bpKey, setBpKey] = useState("");
  const [bpTemplate, setBpTemplate] = useState("");
  const [savingBp, setSavingBp] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setSettings(data);
    setSender(data.SMTP2GO_SENDER || "");
    setSenderName(data.SMTP2GO_SENDER_NAME || "");
    setBpTemplate(data.BETTERPROPOSALS_TEMPLATE_ID || "");
    setApiKey("");
    setLlmKey("");
    setAnthropicKey("");
    setBpKey("");
    // Refresh AI usage stats alongside settings
    try {
      const { data: usage } = await api.get("/admin/ai-usage");
      setAiUsage(usage);
    } catch { /* usage endpoint may not exist on older backends */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { SMTP2GO_SENDER: sender, SMTP2GO_SENDER_NAME: senderName };
      if (apiKey.trim()) payload.SMTP2GO_API_KEY = apiKey.trim();
      await api.patch("/admin/settings", payload);
      toast.success("Settings saved"); await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const clearKey = async () => {
    if (!confirm("Clear the SMTP2Go API key? Emails will fall back to mock mode.")) return;
    await api.patch("/admin/settings", { SMTP2GO_API_KEY: "" });
    toast.success("API key cleared"); load();
  };
  const saveLlm = async () => {
    if (!llmKey.trim()) return;
    setSavingLlm(true);
    try {
      await api.patch("/admin/settings", { EMERGENT_LLM_KEY: llmKey.trim() });
      toast.success("Emergent LLM key saved — AI drafting is live");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSavingLlm(false); }
  };
  const saveLlmKeys = async () => {
    setSavingLlm(true);
    try {
      const patch = {};
      if (anthropicKey.trim()) patch.ANTHROPIC_API_KEY = anthropicKey.trim();
      if (llmKey.trim()) patch.EMERGENT_LLM_KEY = llmKey.trim();
      if (!Object.keys(patch).length) { setSavingLlm(false); return; }
      await api.patch("/admin/settings", patch);
      toast.success("AI keys saved — drafting is live");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSavingLlm(false); }
  };
  const clearLlm = async () => {
    if (!confirm("Clear the Emergent LLM key? AI quote drafting will fall back to the Anthropic key (if set) or stop working.")) return;
    await api.patch("/admin/settings", { EMERGENT_LLM_KEY: "" });
    toast.success("Emergent key cleared"); load();
  };
  const clearAnthropic = async () => {
    if (!confirm("Clear the Anthropic API key? AI drafting will fall back to the Emergent key (if set) or stop working.")) return;
    await api.patch("/admin/settings", { ANTHROPIC_API_KEY: "" });
    toast.success("Anthropic key cleared"); load();
  };
  const [testingLlm, setTestingLlm] = useState(false);
  const [llmResult, setLlmResult] = useState(null); // {ok, reply, model, error, hint}
  const testLlm = async () => {
    setTestingLlm(true);
    setLlmResult(null);
    try {
      const { data } = await api.post("/admin/settings/test-claude");
      setLlmResult({ ok: true, ...data });
      toast.success(`Claude replied: "${(data.reply || "").slice(0, 40)}"`);
    } catch (ex) {
      const d = ex.response?.data?.detail;
      const detail = typeof d === "string" ? { error: d } : (d || { error: "Unknown error" });
      setLlmResult({ ok: false, ...detail });
      toast.error(detail.hint || detail.error || "Test failed");
    } finally { setTestingLlm(false); }
  };
  const saveBp = async () => {
    setSavingBp(true);
    try {
      const payload = { BETTERPROPOSALS_TEMPLATE_ID: bpTemplate.trim() };
      if (bpKey.trim()) payload.BETTERPROPOSALS_API_KEY = bpKey.trim();
      await api.patch("/admin/settings", payload);
      toast.success("BetterProposals settings saved");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSavingBp(false); }
  };
  const clearBp = async () => {
    if (!confirm("Clear the BetterProposals API key? 'Push to BetterProposals' will stop working until you paste a new one.")) return;
    await api.patch("/admin/settings", { BETTERPROPOSALS_API_KEY: "" });
    toast.success("BetterProposals key cleared"); load();
  };
  const testEmail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/test-email");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch { toast.error("Test email failed — check server logs"); }
    finally { setTesting(false); }
  };

  if (!settings) return <Layout><div className="p-10 tech-label">LOADING…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="chip mb-3">// admin · settings</div>
        <h1 className="font-display text-5xl md:text-6xl mb-8">System Settings</h1>

        <div className="sticker p-6 md:p-8" data-testid="smtp2go-settings">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[var(--gc-orange)] text-white flex items-center justify-center border-2 border-[var(--gc-navy)]">
              <Envelope size={22} weight="bold" />
            </div>
            <h2 className="font-display text-3xl">SMTP2Go Email</h2>
          </div>
          <p className="text-sm text-[var(--gc-text-muted)] mb-6">
            Transactional emails for onboarding, quote-ready, design-ready and shipping notifications. Get your API key from <a href="https://app.smtp2go.com" target="_blank" rel="noreferrer" className="text-[var(--gc-orange)] font-semibold hover:underline">app.smtp2go.com</a> → Settings → API Keys.
          </p>

          <div className="mb-6">
            {settings.SMTP2GO_API_KEY_set ? (
              <div className="inline-flex items-center gap-2 text-sm text-[var(--gc-success)] font-bold border-2 border-[var(--gc-success)] bg-green-50 px-4 py-2">
                <CheckCircle size={18} weight="fill"/> Active — Key: {settings.SMTP2GO_API_KEY}
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 text-sm text-[var(--gc-warning)] font-bold border-2 border-[var(--gc-warning)] bg-yellow-50 px-4 py-2">
                <Warning size={18} weight="fill"/> Not configured — emails currently mocked
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <div className="tech-label mb-2 flex items-center gap-1"><Key size={12} weight="bold"/> API Key</div>
              <div className="flex gap-2">
                <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} type="password"
                  placeholder={settings.SMTP2GO_API_KEY_set ? "•••• Enter new key to replace ••••" : "api-xxxxxxxxxxxxxxxxxxx"}
                  className="gc-input font-mono-tech flex-1"
                  data-testid="smtp2go-api-key-input" />
                {settings.SMTP2GO_API_KEY_set && (
                  <button onClick={clearKey} className="border-2 border-[var(--gc-danger)] text-[var(--gc-danger)] hover:bg-red-50 font-semibold text-xs px-3" data-testid="clear-key-btn">
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="tech-label mb-2">Sender Email</div>
                <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="hello@geekcreative.ie" className="gc-input" data-testid="smtp2go-sender-input" />
                <div className="tech-label mt-1 text-[var(--gc-text-muted)]">Must be a verified domain in SMTP2Go</div>
              </div>
              <div>
                <div className="tech-label mb-2">Sender Name</div>
                <input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="GeekCreative" className="gc-input" data-testid="smtp2go-sender-name-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button onClick={save} disabled={saving} className="btn-primary px-5 py-2.5 disabled:opacity-50" data-testid="save-settings-btn">
                {saving ? "Saving…" : "Save Settings"}
              </button>
              {settings.SMTP2GO_API_KEY_set && (
                <button onClick={testEmail} disabled={testing} className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5" data-testid="test-email-btn">
                  <TestTube size={16} weight="bold"/> {testing ? "Sending…" : "Send Test Email"}
                </button>
              )}
            </div>
          </div>
        </div>

        <EmailTemplatesSection />

        <ProjectTypesSection />

        <AdminUsersSection currentAdminEmail={settings.current_admin_email} />

        {/* AI Quote Drafter keys — Anthropic Direct or Emergent Universal */}
        <div className="sticker p-6 mt-6" data-testid="llm-key-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[var(--gc-orange)] text-white flex items-center justify-center border-2 border-[var(--gc-navy)]">
              <Sparkle size={22} weight="bold" />
            </div>
            <h2 className="font-display text-3xl">AI Quote Drafter</h2>
          </div>
          <p className="text-sm text-[var(--gc-text-muted)] mb-4">
            Powers the AI Quote Draft on each job (Claude Sonnet 4.5). Two ways to authorise — an <strong>Anthropic API key</strong> talks directly to Anthropic (recommended for self-hosted deploys — bypasses the Emergent gateway which can 403 datacenter IPs), or the <strong>Emergent Universal LLM key</strong> as a fallback. If both are set, the Anthropic key wins.
          </p>
          {(settings.ANTHROPIC_API_KEY_set || settings.EMERGENT_LLM_KEY_set) ? (
            <div className="flex items-center gap-2 text-[var(--gc-success)] font-mono-tech text-sm mb-4" data-testid="llm-key-active">
              <CheckCircle size={18} weight="fill"/> Active — {settings.ANTHROPIC_API_KEY_set ? `Anthropic direct (${settings.ANTHROPIC_API_KEY})` : `Emergent (${settings.EMERGENT_LLM_KEY})`}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[var(--gc-orange)] font-mono-tech text-sm mb-4" data-testid="llm-key-missing">
              <Warning size={18} weight="fill"/> Not configured — AI drafting is disabled
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <div className="tech-label mb-2 flex items-center gap-1"><Key size={12} weight="bold"/> Anthropic API Key — recommended</div>
              <input
                type="password"
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
                placeholder={settings.ANTHROPIC_API_KEY_set ? "•••• Enter new key to replace ••••" : "sk-ant-api03-xxxxxxxx…"}
                className="gc-input font-mono-tech w-full"
                data-testid="anthropic-key-input"
              />
              <div className="text-xs text-[var(--gc-text-muted)] mt-1">Get one at <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="text-[var(--gc-orange)] hover:underline">console.anthropic.com</a>. Pay-as-you-go, ~€0.01-0.02 per draft.</div>
            </div>
            <div>
              <div className="tech-label mb-2 flex items-center gap-1"><Key size={12} weight="bold"/> Emergent Universal LLM Key — fallback</div>
              <input
                type="password"
                value={llmKey}
                onChange={(e) => setLlmKey(e.target.value)}
                placeholder={settings.EMERGENT_LLM_KEY_set ? "•••• Enter new key to replace ••••" : "sk-emergent-xxxxxxxxxxxxxxxxxx"}
                className="gc-input font-mono-tech w-full"
                data-testid="llm-key-input"
              />
              <div className="text-xs text-[var(--gc-text-muted)] mt-1">From Emergent → Profile → Universal Key. Works in preview but may 403 from self-hosted VPS IPs.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-center pt-2">
            <button onClick={saveLlmKeys} disabled={savingLlm || (!llmKey.trim() && !anthropicKey.trim())} className="btn-primary px-5 py-2.5 disabled:opacity-40" data-testid="llm-key-save">
              {savingLlm ? "Saving…" : "Save Keys"}
            </button>
            <button onClick={testLlm} disabled={testingLlm || (!settings.EMERGENT_LLM_KEY_set && !settings.ANTHROPIC_API_KEY_set)} className="btn-secondary inline-flex items-center gap-2 px-4 py-2.5 disabled:opacity-40" data-testid="llm-key-test">
              <TestTube size={14} weight="bold" /> {testingLlm ? "Pinging Claude…" : "Test Claude connection"}
            </button>
            {settings.ANTHROPIC_API_KEY_set && (
              <button onClick={clearAnthropic} className="text-sm text-[var(--gc-text-muted)] link-underline" data-testid="anthropic-key-clear">Clear Anthropic</button>
            )}
            {settings.EMERGENT_LLM_KEY_set && (
              <button onClick={clearLlm} className="text-sm text-[var(--gc-text-muted)] link-underline" data-testid="llm-key-clear">Clear Emergent</button>
            )}
          </div>
          {aiUsage && (
            <div className="mt-4 border-2 border-[var(--gc-navy)] bg-[var(--gc-cream,#fdf8f0)] px-4 py-3" data-testid="ai-usage-stats">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div className="tech-label">// AI usage · {new Date(aiUsage.month_start).toLocaleDateString(undefined, {month: 'long', year: 'numeric'})}</div>
                <div className="text-xs text-[var(--gc-text-muted)] font-mono-tech">tokens billed against your Anthropic key only</div>
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-2xl font-display" data-testid="ai-usage-drafts">{aiUsage.drafts_generated}</div>
                  <div className="text-xs text-[var(--gc-text-muted)]">drafts generated</div>
                </div>
                <div>
                  <div className="text-2xl font-display">{(aiUsage.input_tokens + aiUsage.output_tokens).toLocaleString()}</div>
                  <div className="text-xs text-[var(--gc-text-muted)]">total tokens · {aiUsage.input_tokens.toLocaleString()} in / {aiUsage.output_tokens.toLocaleString()} out</div>
                </div>
                <div>
                  <div className="text-2xl font-display" data-testid="ai-usage-cost-usd">${aiUsage.total_cost_usd.toFixed(2)}</div>
                  <div className="text-xs text-[var(--gc-text-muted)]">USD spent (approx.)</div>
                </div>
                <div>
                  <div className="text-2xl font-display text-[var(--gc-orange)]" data-testid="ai-usage-cost-eur">€{aiUsage.total_cost_eur.toFixed(2)}</div>
                  <div className="text-xs text-[var(--gc-text-muted)]">EUR (~0.92 rate)</div>
                </div>
              </div>
              {aiUsage.recent && aiUsage.recent.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-[var(--gc-text-muted)] font-mono-tech">Show recent drafts ({aiUsage.recent.length})</summary>
                  <table className="w-full mt-2 text-xs font-mono-tech">
                    <thead className="text-[var(--gc-text-muted)]">
                      <tr>
                        <th className="text-left py-1">when</th>
                        <th className="text-left py-1">job</th>
                        <th className="text-right py-1">tokens</th>
                        <th className="text-right py-1">cost</th>
                        <th className="text-right py-1">route</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsage.recent.map((r, i) => (
                        <tr key={i} className="border-t border-[var(--gc-border)]">
                          <td className="py-1">{new Date(r.created_at).toLocaleString()}</td>
                          <td className="py-1">{r.job_number || r.job_id?.slice(0, 8)}</td>
                          <td className="py-1 text-right">{((r.input_tokens || 0) + (r.output_tokens || 0)).toLocaleString()}</td>
                          <td className="py-1 text-right">${r.cost_usd.toFixed(4)}</td>
                          <td className="py-1 text-right">{r.route}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
          {llmResult && (
            <div
              className={`mt-4 text-sm border-2 px-4 py-3 ${llmResult.ok ? "bg-[var(--gc-cream,#fdf8f0)] border-[var(--gc-success)]" : "bg-[#fff5f2] border-[var(--gc-orange)]"}`}
              data-testid={llmResult.ok ? "llm-test-success" : "llm-test-failure"}
            >
              {llmResult.ok ? (
                <>
                  <div className="flex items-center gap-2 font-semibold text-[var(--gc-success)]">
                    <CheckCircle size={18} weight="fill" /> Connection OK — Claude replied
                  </div>
                  <div className="mt-1 text-[var(--gc-text-muted)]">
                    <span className="font-mono-tech">reply:</span> {`"${llmResult.reply}"`}
                    {llmResult.model && <span className="ml-3 font-mono-tech text-xs">· model: {llmResult.model}</span>}
                    {llmResult.app_url && <span className="ml-3 font-mono-tech text-xs">· X-App-ID: {llmResult.app_url}</span>}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 font-semibold text-[var(--gc-orange)]">
                    <Warning size={18} weight="fill" /> Connection failed
                  </div>
                  {llmResult.hint && <div className="mt-1"><strong>Likely cause:</strong> {llmResult.hint}</div>}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-[var(--gc-text-muted)] font-mono-tech">Show raw error</summary>
                    <pre className="mt-1 text-xs whitespace-pre-wrap break-all bg-white border border-[var(--gc-border)] p-2 font-mono-tech">{llmResult.error}</pre>
                  </details>
                </>
              )}
            </div>
          )}
        </div>

        {/* BetterProposals — one-click push of AI quote draft */}
        <div className="sticker p-6 mt-6" data-testid="bp-key-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[var(--gc-navy)] text-white flex items-center justify-center border-2 border-[var(--gc-navy)]">
              <FileText size={22} weight="bold" />
            </div>
            <h2 className="font-display text-3xl">BetterProposals</h2>
          </div>
          <p className="text-sm text-[var(--gc-text-muted)] mb-4">
            One-click creation of a new BetterProposals proposal from your template, with the customer attached as the signing recipient and 5 metadata merge tags pre-filled. Grab your API token at <a href="https://betterproposals.io/2/integrations/api/" target="_blank" rel="noreferrer" className="text-[var(--gc-orange)] font-semibold hover:underline">Better Proposals → Integrations → API</a>. The Template ID is in the URL when editing a template (e.g. <span className="font-mono-tech">…/templates/edit?id=<strong>738606</strong></span>).
          </p>
          <details className="mb-6 border border-[var(--gc-border)] bg-[var(--gc-cream,#fdf8f0)] px-4 py-3 text-sm">
            <summary className="cursor-pointer font-semibold">Which custom merge tags do I need to create in Better Proposals?</summary>
            <div className="mt-3 text-[var(--gc-text-muted)] space-y-2">
              <p>In Better Proposals → <strong>Settings → Setup → Custom Merge Tags</strong>, add these 5 tags. They pre-fill the proposal with the customer&apos;s details when you click <strong>Push to BetterProposals</strong>. The AI-drafted quote sections are pasted separately using <strong>Copy HTML</strong> on the draft panel — Better Proposals&apos; API caps content per call at ~1&nbsp;KB, so long content is done by hand in BP&apos;s editor.</p>
              <ul className="font-mono-tech text-xs space-y-1 mt-3">
                <li>gc_contact_name  <span className="text-[var(--gc-navy)]">— customer&apos;s full name</span></li>
                <li>gc_company_name  <span className="text-[var(--gc-navy)]">— business name (or personal name if none)</span></li>
                <li>gc_project_name  <span className="text-[var(--gc-navy)]">— project name from the brief</span></li>
                <li>gc_project_type  <span className="text-[var(--gc-navy)]">— e.g. {`"Logo & Branding"`}</span></li>
                <li>gc_job_number    <span className="text-[var(--gc-navy)]">— GC job reference</span></li>
              </ul>
              <p className="mt-3">Use them in your template like <span className="font-mono-tech">{"{{gc_contact_name}}"}</span>, <span className="font-mono-tech">{"{{gc_company_name}}"}</span>, etc. Any tag you haven&apos;t registered yet is silently skipped — the push still succeeds.</p>
              <button
                onClick={() => { navigator.clipboard?.writeText(['gc_contact_name','gc_company_name','gc_project_name','gc_project_type','gc_job_number'].join('\n')); toast.success("5 tag names copied"); }}
                className="btn-secondary inline-flex items-center gap-2 text-xs px-3 py-1.5 mt-2"
                data-testid="bp-copy-tags"
              >
                <Copy size={12} weight="bold"/> Copy tag names
              </button>
            </div>
          </details>
          {settings.BETTERPROPOSALS_API_KEY_set ? (
            <div className="flex items-center gap-2 text-[var(--gc-success)] font-mono-tech text-sm mb-4" data-testid="bp-key-active">
              <CheckCircle size={18} weight="fill"/> Active — Key: {settings.BETTERPROPOSALS_API_KEY}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[var(--gc-orange)] font-mono-tech text-sm mb-4" data-testid="bp-key-missing">
              <Warning size={18} weight="fill"/> Not configured — Push to BetterProposals is disabled
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="tech-label mb-2 flex items-center gap-1"><Key size={12} weight="bold"/> API Token</div>
              <input
                type="password"
                value={bpKey}
                onChange={(e) => setBpKey(e.target.value)}
                placeholder={settings.BETTERPROPOSALS_API_KEY_set ? "•••• Enter new key to replace ••••" : "bp-xxxxxxxxxxxxxxxxxx"}
                className="gc-input font-mono-tech w-full"
                data-testid="bp-key-input"
              />
            </div>
            <div>
              <div className="tech-label mb-2">Template ID</div>
              <input
                type="text"
                value={bpTemplate}
                onChange={(e) => setBpTemplate(e.target.value)}
                placeholder="e.g. 116768"
                className="gc-input font-mono-tech w-full"
                data-testid="bp-template-input"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={saveBp} disabled={savingBp || (!bpKey.trim() && !bpTemplate.trim())} className="btn-primary px-5 py-2.5 disabled:opacity-40" data-testid="bp-save">
              {savingBp ? "Saving…" : "Save BetterProposals Settings"}
            </button>
            {settings.BETTERPROPOSALS_API_KEY_set && (
              <button onClick={clearBp} className="text-sm text-[var(--gc-text-muted)] link-underline" data-testid="bp-clear">Clear key</button>
            )}
          </div>
        </div>

        <div className="sticker p-6 mt-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[var(--gc-navy)] text-white flex items-center justify-center border-2 border-[var(--gc-navy)]">
              <Database size={22} weight="bold" />
            </div>
            <h2 className="font-display text-2xl">File Storage</h2>
          </div>
          <div className="mt-3 text-sm text-[var(--gc-navy)]">All uploaded assets (customer files, design iterations, final proofs) are stored directly inside MongoDB via GridFS — nothing external. Portable and ready to self-host on Hetzner.</div>
        </div>
      </div>
    </Layout>
  );
}
